import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Download, FileText, Filter, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';

// =====================================================================
// Tela INICIAL do sub-hub "Auditoria Financeira e de Estoques" (módulo
// Operacional). Morava no módulo Auditoria até a DEM-00011 (Fase 4, T21).
//
// DEM-00011 (Fase 4, 2026-09-23): redesenhada no padrão da Avaliação 360
// (Aval360Historico.js) a pedido da Julia, depois de comparar as duas telas
// lado a lado no mesmo hub — LISTA PLANA com filtros AO VIVO (client-side,
// sem botão "Buscar"), legenda das faixas, linha clicável abrindo o detalhe e
// exportação em Excel direto no navegador (sem round-trip ao servidor).
//
// ⚠️ UMA DIFERENÇA REAL, não de estilo: a 360 mostra "Evidências" (fotos) por
// linha porque tem tabela própria de fotos; o Checklist NÃO TEM — as fotos são
// embutidas SÓ NO PDF no momento da geração (decisão de desenho: "modelo
// enxuto", nunca ficam gravadas à parte). Por isso não há essa coluna aqui —
// adicioná-la exigiria uma tabela nova, não é só CSS.
//
// ⚠️ DUAS RÉGUAS CONVIVEM (DEM-00011, T15): cada auditoria usa a régua da sua
// VERSÃO (METODO = 'PESO' legado ou 'CONTAGEM' nova). A legenda mostra as
// duas, e o filtro de Classificação lista só os valores que aparecem nos
// dados carregados (nunca as 9 fixas) — não faz sentido oferecer "Vermelho"
// se nenhuma auditoria CONTAGEM foi feita ainda.
//
// `acoesExtras`: nó opcional com botões renderizado no cabeçalho — "Nova
// Auditoria" + "Gerenciar versões", montados pelo `OperacionalChecklist`.
// =====================================================================

const API_BASE = '/api/auditoria/checklist';
const COR = '#475569'; // ardósia do módulo Operacional
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Estilo de cada classificação — as DUAS réguas na mesma coluna (a versão
// decide qual vale; nunca as duas ao mesmo tempo na mesma linha).
// ⚠️ Era risco (Baixo/Medio/Critico/AltoRisco) até 2026-09-04; as auditorias
// antigas foram convertidas no banco, então esses rótulos não voltam.
const corClassificacao = (c) => {
  if (c === 'Excelente')    return { bg: '#d1fae5', color: '#065f46', icone: '🟢', rotulo: 'Excelente' };
  if (c === 'Satisfatorio') return { bg: '#ecfccb', color: '#3f6212', icone: '🟢', rotulo: 'Satisfatório' };
  if (c === 'Atencao')      return { bg: '#fef3c7', color: '#92400e', icone: '🟡', rotulo: 'Atenção' };
  if (c === 'Critico')      return { bg: '#fee2e2', color: '#991b1b', icone: '🔴', rotulo: 'Crítico' };
  if (c === 'NA')           return { bg: '#f1f5f9', color: '#475569', icone: '⚪', rotulo: 'N/A' };
  if (c === 'Verde')        return { bg: '#d1fae5', color: '#065f46', icone: '🟢', rotulo: 'Verde' };
  if (c === 'Amarelo')      return { bg: '#fef9c3', color: '#854d0e', icone: '🟡', rotulo: 'Amarelo' };
  if (c === 'Laranja')      return { bg: '#ffedd5', color: '#9a3412', icone: '🟠', rotulo: 'Laranja' };
  if (c === 'Vermelho')     return { bg: '#fee2e2', color: '#991b1b', icone: '🔴', rotulo: 'Vermelho' };
  return { bg: '#f1f5f9', color: '#475569', icone: '', rotulo: c || '-' };
};

// Rótulo curto do método, para a coluna/legenda dizer qual régua vale (T19).
const rotuloMetodo = (m) => (m === 'CONTAGEM' ? 'Contagem' : 'Peso');

// As duas réguas, para a legenda — SEMPRE as duas (não só as em uso agora):
// uma auditoria PESO nova ainda pode acontecer numa versão antiga não
// arquivada, então a legenda não pode presumir que só a nova está viva.
const FAIXAS_PESO = [
  { classif: 'Excelente', faixa: '90 a 100%' },
  { classif: 'Satisfatorio', faixa: '80 a 89,99%' },
  { classif: 'Atencao', faixa: '60 a 79,99%' },
  { classif: 'Critico', faixa: 'abaixo de 60%' },
];
const FAIXAS_CONTAGEM = [
  { classif: 'Verde', faixa: '90 a 100%' },
  { classif: 'Amarelo', faixa: '80 a 89,99%' },
  { classif: 'Laranja', faixa: '70 a 79,99%' },
  { classif: 'Vermelho', faixa: 'abaixo de 70%' },
];

const formatarDataBR = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};
const dataRef = (a) => String(a.DATA_AUDITORIA || '').slice(0, 10);

const ITENS_POR_PAGINA = 25;

// `metodo`: quando informado ('PESO' | 'CONTAGEM'), a tela fica PRESA a esse
// método — usado pelas duas abas do card (OperacionalChecklist.js), no mesmo
// padrão da Avaliação 360 × Histórico BSV. Aqui as duas séries vêm da MESMA
// tabela (dtbAuditoriaChecklist), então em vez de dois componentes (como
// Aval360Historico × BsvHistorico, que são tabelas diferentes) um só filtra
// por escopo — reaproveita tudo (filtros, paginação, export) sem duplicar.
// `titulo`: rótulo do cabeçalho, diferente em cada aba.
const ChecklistHistorico = ({ acoesExtras, metodo, titulo = 'Auditorias gravadas' }) => {
  const [auditorias, setAuditorias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [detalheId, setDetalheId] = useState(null);
  const [abrindoPdf, setAbrindoPdf] = useState(null);

  // Filtros (client-side, instantâneos — mesmo padrão da Avaliação 360).
  const [fLoja, setFLoja] = useState('');
  const [fVersao, setFVersao] = useState('');
  const [fMetodo, setFMetodo] = useState('');
  const [fClassif, setFClassif] = useState('');
  const [fPeriodo, setFPeriodo] = useState(''); // mês de referência 'AAAA-MM'
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API_BASE}/historico`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setAuditorias(await r.json());
    } catch (e) {
      setErro('Erro ao carregar as auditorias.');
      console.error(e);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Escopo da aba: quando `metodo` vem fixo (as duas abas do card sempre
  // passam um), tudo abaixo — filtros, legenda, paginação, export — opera só
  // sobre esse recorte. Sem `metodo` (uso avulso, se algum dia houver), mostra
  // tudo — é o comportamento anterior às abas.
  const auditoriasDaAba = useMemo(
    () => (metodo ? auditorias.filter((a) => a.METODO === metodo) : auditorias),
    [auditorias, metodo]
  );

  const lojas = useMemo(() => [...new Set(auditoriasDaAba.map((a) => a.LOJA).filter(Boolean))].sort(), [auditoriasDaAba]);
  const versoes = useMemo(() => [...new Set(auditoriasDaAba.map((a) => a.NOME_VERSAO).filter(Boolean))].sort(), [auditoriasDaAba]);
  // Só os métodos que de fato aparecem NO ESCOPO — com `metodo` fixo isto dá
  // sempre 1 valor, então o seletor de Método se esconde sozinho (não precisa
  // de outra condição): filtrar por ele não mudaria nada.
  const metodos = useMemo(() => [...new Set(auditoriasDaAba.map((a) => a.METODO).filter(Boolean))].sort(), [auditoriasDaAba]);
  const classificacoes = useMemo(
    () => [...new Set(auditoriasDaAba.map((a) => a.CLASSIFICACAO_RISCO).filter(Boolean))],
    [auditoriasDaAba]
  );

  const filtradas = useMemo(() => auditoriasDaAba.filter((a) => {
    if (fLoja && a.LOJA !== fLoja) return false;
    if (fVersao && a.NOME_VERSAO !== fVersao) return false;
    if (fMetodo && a.METODO !== fMetodo) return false;
    if (fClassif && a.CLASSIFICACAO_RISCO !== fClassif) return false;
    const d = dataRef(a);
    if (fPeriodo && (!d || d.slice(0, 7) !== fPeriodo)) return false;
    return true;
  }), [auditoriasDaAba, fLoja, fVersao, fMetodo, fClassif, fPeriodo]);

  useEffect(() => { setPagina(1); }, [fLoja, fVersao, fMetodo, fClassif, fPeriodo]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const linhas = filtradas.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);

  const temFiltro = fLoja || fVersao || fMetodo || fClassif || fPeriodo;
  const limparFiltros = () => { setFLoja(''); setFVersao(''); setFMetodo(''); setFClassif(''); setFPeriodo(''); };

  // Abre o PDF numa aba nova. Por fetch (a rota exige token; <a href> daria 401
  // — ver CLAUDE.md › "Rota autenticada só se alcança por fetch").
  const abrirPdf = useCallback(async (id) => {
    setAbrindoPdf(id);
    try {
      const r = await fetch(`${API_BASE}/auditoria/${id}?formato=pdf`, { headers: auth() });
      if (!r.ok) throw new Error('Não foi possível abrir o PDF desta auditoria.');
      const url = window.URL.createObjectURL(await r.blob());
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message);
    } finally {
      setAbrindoPdf(null);
    }
  }, []);

  // Exporta a lista FILTRADA, no navegador — nada de round-trip ao servidor
  // (mesmo padrão da Avaliação 360). O detalhe (respostas pergunta a pergunta)
  // segue exportável em XLSX próprio, na tela de detalhe.
  const exportarExcel = () => {
    const dados = filtradas.map((a) => ({
      'Auditoria': a.ID, 'Versão': a.NOME_VERSAO, Método: rotuloMetodo(a.METODO), Loja: a.LOJA,
      Data: formatarDataBR(a.DATA_AUDITORIA),
      Auditor: a.AUDITOR_RESPONSAVEL || '', 'Gerente de Negócios': a.GERENTE_NEGOCIOS || '',
      'Gerente de Plantão': a.GERENTE_PLANTAO || '',
      'Score': a.SCORE_TOTAL, 'Percentual (%)': a.PERCENTUAL_RISCO_TOTAL,
      'Classificação': corClassificacao(a.CLASSIFICACAO_RISCO).rotulo,
      'Recomendações': a.RECOMENDACOES || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dados), 'Auditorias');
    XLSX.writeFile(wb, `Auditorias_Financeira_Estoques_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (detalheId) return <DetalheAuditoria id={detalheId} onVoltar={() => setDetalheId(null)} />;

  const th = { padding: '13px 16px', fontWeight: 700, borderBottom: '1px solid #e2e8f0', fontSize: 12.5, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
  const td = { padding: '13px 16px', color: '#334155', fontSize: 14, whiteSpace: 'nowrap' };
  const selStyle = { width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none', background: '#fff', color: '#475569', fontSize: 13 };

  return (
    <div>
      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 180, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{titulo}</h3>
        {acoesExtras}
        {filtradas.length > 0 && (
          <button onClick={exportarExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <Download size={16} /> Exportar Excel
          </button>
        )}
      </div>

      {/* FILTROS (mesmo cartão da Avaliação 360) */}
      <div style={{ backgroundColor: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 150px' }}>
          <label style={filtroLbl}>Loja</label>
          <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={selStyle}>
            <option value="">Todas</option>
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={filtroLbl}>Formulário</label>
          <select value={fVersao} onChange={(e) => setFVersao(e.target.value)} style={selStyle}>
            <option value="">Todas</option>
            {versoes.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {metodos.length > 1 && (
          <div style={{ flex: '1 1 130px' }}>
            <label style={filtroLbl}>Método</label>
            <select value={fMetodo} onChange={(e) => setFMetodo(e.target.value)} style={selStyle}>
              <option value="">Todos</option>
              {metodos.map((m) => <option key={m} value={m}>{rotuloMetodo(m)}</option>)}
            </select>
          </div>
        )}
        <div style={{ flex: '1 1 150px' }}>
          <label style={filtroLbl}>Classificação</label>
          <select value={fClassif} onChange={(e) => setFClassif(e.target.value)} style={selStyle}>
            <option value="">Todas</option>
            {classificacoes.map((c) => <option key={c} value={c}>{corClassificacao(c).rotulo}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={filtroLbl}>Período (mm/aaaa)</label>
          <input type="month" value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)} style={selStyle} />
        </div>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Filter size={15} /> Limpar
          </button>
        )}
      </div>

      {/* Legenda — com `metodo` fixo (as duas abas do card sempre passam um),
          mostra só a régua daquela aba; sem ele, as duas (uso avulso). */}
      {metodo !== 'CONTAGEM' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: metodo ? 16 : 6, paddingLeft: 2 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>{metodo ? 'Régua (Peso, legado)' : 'Peso (legado)'}</span>
          {FAIXAS_PESO.map(({ classif, faixa }) => {
            const c = corClassificacao(classif);
            return (
              <span key={classif} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                {c.rotulo}<span style={{ fontWeight: 600, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{faixa}</span>
              </span>
            );
          })}
        </div>
      )}
      {metodo !== 'PESO' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16, paddingLeft: 2 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>{metodo ? 'Régua (Contagem, nova)' : 'Contagem (nova)'}</span>
          {FAIXAS_CONTAGEM.map(({ classif, faixa }) => {
            const c = corClassificacao(classif);
            return (
              <span key={classif} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                {c.rotulo}<span style={{ fontWeight: 600, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{faixa}</span>
              </span>
            );
          })}
        </div>
      )}

      {erro && <div style={alerta}>{erro}</div>}

      {/* LISTA PLANA */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 900 }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={th}>Loja</th>
                <th style={th}>Versão</th>
                {!metodo && <th style={th}>Método</th>}
                <th style={th}>Data</th>
                <th style={th}>Auditor</th>
                <th style={th}>G. Negócios</th>
                <th style={th}>G. Plantão</th>
                <th style={{ ...th, textAlign: 'right' }}>Nota</th>
                <th style={{ ...th, textAlign: 'center' }}>Classificação</th>
                <th style={{ ...th, textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && <tr><td colSpan={metodo ? 9 : 10} style={{ ...td, textAlign: 'center', color: '#64748b' }}>Carregando…</td></tr>}
              {!carregando && filtradas.length === 0 && (
                <tr><td colSpan={metodo ? 9 : 10} style={{ ...td, textAlign: 'center', color: '#64748b' }}>
                  {auditoriasDaAba.length === 0 ? 'Nenhuma auditoria gravada ainda.' : 'Nenhuma auditoria para os filtros escolhidos.'}
                </td></tr>
              )}
              {linhas.map((a) => {
                const c = corClassificacao(a.CLASSIFICACAO_RISCO);
                return (
                  <tr key={a.ID} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onClick={() => setDetalheId(a.ID)}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <td style={{ ...td, fontWeight: 700, color: '#0f172a' }}>{a.LOJA}</td>
                    <td style={{ ...td, whiteSpace: 'normal', color: '#475569' }}>{a.NOME_VERSAO}</td>
                    {!metodo && <td style={{ ...td, color: '#64748b' }}>{rotuloMetodo(a.METODO)}</td>}
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{formatarDataBR(a.DATA_AUDITORIA) || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.AUDITOR_RESPONSAVEL || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.GERENTE_NEGOCIOS || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{a.GERENTE_PLANTAO || '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 800, color: c.color, fontVariantNumeric: 'tabular-nums' }}>{a.PERCENTUAL_RISCO_TOTAL}%</span>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>score {a.SCORE_TOTAL}</span>
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 800, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>
                        {c.icone} {c.rotulo}
                      </span>
                    </td>
                    {/* Ações não propagam o clique da linha (senão abririam o detalhe junto). */}
                    <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button onClick={() => setDetalheId(a.ID)} title="Ver detalhe por área"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                          Detalhe
                        </button>
                        <button onClick={() => abrirPdf(a.ID)} disabled={abrindoPdf === a.ID} title="Abrir o PDF (com as fotos das perguntas Não)"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: `1px solid ${COR}`, background: '#fff', color: COR, fontWeight: 700, fontSize: 12, cursor: abrindoPdf === a.ID ? 'wait' : 'pointer' }}>
                          {abrindoPdf === a.ID ? <Loader2 size={13} className="spin" /> : <FileText size={13} />}
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Rodapé de paginação */}
        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Página {paginaAtual} de {totalPaginas}
            <span style={{ marginLeft: 10, fontWeight: 600 }}>(Total: {filtradas.length} auditoria{filtradas.length === 1 ? '' : 's'})</span>
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPagina((p) => Math.max(p - 1, 1))} disabled={paginaAtual === 1}
              style={btnPag(paginaAtual === 1)}>Anterior</button>
            <button onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas}
              style={btnPag(paginaAtual === totalPaginas)}>Próxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const filtroLbl = { display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 };
const alerta = { padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 };
const btnPag = (desab) => ({
  padding: '6px 14px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff',
  color: desab ? '#94a3b8' : '#0f172a', cursor: desab ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

// ---------------------------------------------------------------------
// Detalhe (mesmo arquivo p/ reduzir fragmentação)
// ---------------------------------------------------------------------
const DetalheAuditoria = ({ id, onVoltar }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/auditoria/${id}`, { headers: auth() });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.message || 'Erro');
        setDados(data);
      } catch (e) { setErro(e.message); }
    })();
  }, [id]);

  const exportarDetalhe = async () => {
    try {
      const r = await fetch(`${API_BASE}/auditoria/${id}?formato=xlsx`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Auditoria_${id}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErro('Erro ao exportar: ' + e.message); }
  };

  const exportarDetalhePdf = async () => {
    try {
      const r = await fetch(`${API_BASE}/auditoria/${id}?formato=pdf`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Auditoria_${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErro('Erro ao exportar PDF: ' + e.message); }
  };

  if (erro) return (
    <div>
      <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
      <div style={{ ...alerta, marginTop: 12 }}>{erro}</div>
    </div>
  );
  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const { auditoria, respostas, resumoPorArea = [] } = dados;
  const c = corClassificacao(auditoria.CLASSIFICACAO_RISCO);

  // Agrupa respostas por área
  const porArea = respostas.reduce((acc, r) => {
    if (!acc[r.AREA]) acc[r.AREA] = [];
    acc[r.AREA].push(r);
    return acc;
  }, {});
  const metodoContagem = auditoria.METODO === 'CONTAGEM';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Auditoria #{auditoria.ID} — {auditoria.LOJA} · {formatarDataBR(auditoria.DATA_AUDITORIA)}
        </h3>
        <button onClick={exportarDetalhePdf} style={btnVermelho}>
          <FileText size={16} /> Exportar PDF
        </button>
        <button onClick={exportarDetalhe} style={btnVerde}>
          <Download size={16} /> Exportar XLSX
        </button>
      </div>

      {/* Cabeçalho com resultado — mesmo padrão da Avaliação 360 */}
      <div style={{
        background: c.bg, border: `2px solid ${c.color}`,
        borderRadius: 12, padding: 16, marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 28 }}>{c.icone}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>RESULTADO GERAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>
            score {auditoria.SCORE_TOTAL} · {auditoria.PERCENTUAL_RISCO_TOTAL}% · {c.rotulo}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <div>Versão: <strong>{auditoria.NOME_VERSAO}</strong> · Método: <strong>{rotuloMetodo(auditoria.METODO)}</strong></div>
          <div>Auditor: <strong>{auditoria.AUDITOR_RESPONSAVEL || '-'}</strong></div>
          <div>G. Negócios: <strong>{auditoria.GERENTE_NEGOCIOS || '-'}</strong></div>
          <div>G. Plantão: <strong>{auditoria.GERENTE_PLANTAO || '-'}</strong></div>
        </div>
      </div>

      {/* Score por área */}
      {resumoPorArea.length > 0 && (
        <div style={card}>
          <h4 style={{ margin: '0 0 12px', color: '#0f172a' }}>Score por Área e Classificação</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#475569' }}>
              <tr>
                {['Área', 'Score', '% Conformidade', 'Classificação'].map((t, i) => (
                  <th key={t} style={{ ...th, color: '#fff', borderBottom: 'none', textAlign: i === 0 ? 'left' : 'center' }}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resumoPorArea.map((a) => {
                const ca = corClassificacao(a.classificacao);
                return (
                  <tr key={a.idArea} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={td}>{a.area}</td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{a.classificacao === 'NA' ? '—' : a.score}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{a.classificacao === 'NA' ? '—' : `${Math.round(a.percentualConformidade)}%`}</td>
                    <td style={{ ...td, textAlign: 'center', background: ca.bg, color: ca.color, fontWeight: 700 }}>
                      {ca.icone} {ca.rotulo}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid #475569' }}>
                <td style={{ ...td, fontWeight: 800 }}>RESULTADO GERAL</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 800 }}>{auditoria.SCORE_TOTAL}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 800 }}>{Math.round(auditoria.PERCENTUAL_RISCO_TOTAL)}%</td>
                <td style={{ ...td, textAlign: 'center', background: c.bg, color: c.color, fontWeight: 800 }}>
                  {c.icone} {c.rotulo}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Respostas por área */}
      {Object.entries(porArea).map(([area, perguntas]) => (
        <div key={area} style={card}>
          <h4 style={{ margin: '0 0 12px', color: '#0f172a' }}>{area}</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>Pergunta</th>
                {!metodoContagem && <th style={{ ...th, width: 70, textAlign: 'center' }}>Peso</th>}
                <th style={{ ...th, width: 80, textAlign: 'center' }}>Atende</th>
                <th style={{ ...th, width: 80, textAlign: 'center' }}>Pontos</th>
                <th style={th}>Observação</th>
              </tr>
            </thead>
            <tbody>
              {perguntas.map((p) => (
                <tr key={p.ID_PERGUNTA} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={td}>{p.TEXTO}</td>
                  {!metodoContagem && <td style={{ ...td, textAlign: 'center' }}>{p.PESO}</td>}
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: p.NAO_APLICAVEL ? '#64748b' : (p.ATENDE === 1 ? '#059669' : '#dc2626') }}>
                    {p.NAO_APLICAVEL ? 'N/A' : (p.ATENDE === 1 ? 'Sim' : 'Não')}
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{p.NAO_APLICAVEL ? '—' : p.PONTUACAO}</td>
                  <td style={td}>{p.OBSERVACAO || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {auditoria.RECOMENDACOES && (
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', color: '#0f172a' }}>Recomendações e Plano de Ação</h4>
          <p style={{ margin: 0, color: '#475569', whiteSpace: 'pre-wrap' }}>{auditoria.RECOMENDACOES}</p>
        </div>
      )}
    </div>
  );
};

// estilos
const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 };
const th = { padding: 10, textAlign: 'left', fontWeight: 800, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: 10, color: '#0f172a' };
const btnVerde = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};
const btnVermelho = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};

export default ChecklistHistorico;
